import React, { useState, useEffect, useMemo, useCallback } from 'react';

import SettingsModal from 'src/component/UI/SettingsModal/SettingsModal';

import { useSelector, useDispatch } from 'react-redux';
import { fromJS } from 'immutable';

import { fetchIntegrations } from 'src/module/integration/action';
import { getIntegrations } from 'src/module/integration/selector';
import { fetchControlPanelTags, clearControlPanelTags, setSettingsModalClose, fetchCommLoopTags, setSettingsModalOpen, fetchPanelByQrCode } from 'src/module/device/action';
import { getControlPanelTags, getSettingsModal, getCommLoopTags, getShowSynchronizingDevicesPopUp, getPanelDataFromDirectory } from 'src/module/device/selector';
import { SettingsModalTypes } from 'src/component/UI/SettingsModal/SettingsModalTypes';
import { addController } from 'src/module/controller/action';
import InitDeviceModel from 'src/screen/Protected/DeviceScreen/InitDeviceModel';
import { hasFeaturePermissions } from 'src/module/authentication/selector';
import { useParams } from 'react-router-dom';
import { pushHistory } from 'src/module/navigation/action';
import { useTranslation } from 'src/i18n';
import { buildTcpFields, buildSerialFields, CONNECTION_TYPE_OPTIONS } from 'src/component/UI/commLoopFieldDefs';

function AddControllerModal () {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const { newPanelId } = useParams();
  const integrations = useSelector(getIntegrations, (prev, next) => prev.equals(next));
  const commLoopTags = useSelector(getCommLoopTags, (prev, next) => prev.equals(next));
  const settingsModal = useSelector(getSettingsModal(null), (prev, next) => prev.equals(next));
  const [panelId, setPanelId] = useState(null);
  const [, setParent] = useState(null);

  const [filledValuesObj, setFilledValuesObj] = useState(newPanelId ? { device_factory: 'SmartTraceController' }: {});
  const fieldValuesObj = {};
  const showSyncPopUp = useSelector(getShowSynchronizingDevicesPopUp);
  const hasBetaFeature = useSelector(hasFeaturePermissions('beta'));
  const panelDataFromDirectory = useSelector(getPanelDataFromDirectory(newPanelId));
  if (newPanelId) {
    dispatch(setSettingsModalOpen(null, SettingsModalTypes.ADD_CONTROLLER));
    dispatch(fetchPanelByQrCode(newPanelId));
  }

  useEffect(() => {
    dispatch(clearControlPanelTags());
    dispatch(fetchIntegrations());
    dispatch(fetchCommLoopTags());
    dispatch(fetchControlPanelTags());
    dispatch(setSettingsModalClose(null));
  }, []);

  const frogClients = useMemo(() => {
    const clients = [];
    integrations?.forEach(integration => {
      if (integration.get('type') === 'frog') {
        const frogId = integration?.get('id');
        const frogName = integration?.getIn(['settings', 'name']) || frogId.substring(0, 5);
        clients.push({ label: frogName, value: frogId });
      }
    });
    return clients;
  }, [integrations]);

  const commonControllerFields = useMemo(() => {
    // Basic controller options
    const controllerOptions = [
      { label: 'NVent 910', value: 'NVent910' },
      { label: 'NVent 920', value: 'NVent920' },
      { label: 'NVent NGC30', value: 'NVentNGC30Controller' },
      { label: 'NVent NGC40', value: 'NVentNGC40Controller' },
      { label: 'NVent AC2000+', value: 'NVentAC2000P' },
      { label: 'Thermon TCM2', value: 'ThermonTCM2Controller' },
      { label: 'Thermon TCM18', value: 'ThermonTCM18Controller' },
      { label: 'Thermon TC101', value: 'ThermonTC101Controller' },
      { label: 'Thermon TC201', value: 'ThermonTC201Controller' },
      { label: 'Thermon TC202', value: 'ThermonTC202Controller' },
      { label: 'Thermon TC1818', value: 'ThermonTC1818Controller' },
      { label: 'Chromalox ITLSC1D2', value: 'ChromaloxITLSC1D2EXTController' },
    ];

    // Conditionally add beta-specific options if `features` includes 'beta'
    if (hasBetaFeature) {
      controllerOptions.push(
        { label: 'ATCOM+', value: 'ATCOMPlusController' },
        { label: 'Thermon Genesis', value: 'ThermonGenesisController' },
        { label: 'MasterTrace MR100', value: 'MasterTraceMR100Controller' },
        { label: 'MSCP Modular EHT Controller', value: 'MSCPController' },
        { label: 'SmartTrace Controller', value: 'SmartTraceV2Controller' },
        { label: 'SmartTrace Monitored Panel', value: 'SmartTraceMPController' },
        { label: 'Elexant 3500i', value: 'Elexant3500i' },
        { label: 'Elexant 4020i', value: 'Elexant4020i' },
        { label: 'Elexant 4010i', value: 'Elexant4010i' },
      );
    }

    const commLoopOptions = [{ label: 'Add Comm Loop', value: 'null' }];
    commLoopTags?.forEach(commLoop => {
      commLoopOptions.push({ label: commLoop.get('tag'), value: commLoop.get('id') });
    });

    return {
      device_factory: {
        type: 'select',
        label: 'Device Type',
        options: controllerOptions,
        errorText: 'Required',
        binding: true,
        order: 10
      },
      parent_id: {
        type: 'autoCompleteSelect',
        label: 'Comm Loop',
        options: commLoopOptions,
        errorText: 'Required',
        required: false,
        freeSolo: true,
        formatOption: (option) => ({
          label: option.get('tag'),
          value: option.get('id')
        }),
        onValueChange: (option) => {
          setParent(option);
        },
        deviceType: 'commLoop',
        optionsSelector: getCommLoopTags,
        order: 30
      },
      tag: {
        type: 'text',
        label: 'Controller Tag',
        errorText: 'Required',
        order: 70
      },
      address: {
        type: 'number',
        label: 'Controller Address',
        errorText: 'Required',
        min: 1,
        max: 255,
        order: 80
      }
    };
  }, [hasBetaFeature, commLoopTags]);

  const [addControllerFields, setAddControllerFields] = useState(commonControllerFields);

  const onValueChange = useCallback((panel) => {
    setPanelId(panel.id);
  }, []);

  const buildInlineCommLoopFields = useCallback((connectionType: 'tcp' | 'serial') => {
    const connectionTypeField = {
      connection_type: {
        type: 'select',
        label: 'Connection Type',
        options: CONNECTION_TYPE_OPTIONS,
        errorText: 'Required',
        order: 35,
      },
    };
    if (connectionType === 'serial') {
      const serial = buildSerialFields(frogClients, t);
      return Object.fromEntries(
        Object.entries({ ...connectionTypeField, ...serial }).map(([k, v], i) => [k, { ...v, order: 35 + i + 1 }])
      );
    }
    const tcp = buildTcpFields(frogClients, t);
    return Object.fromEntries(
      Object.entries({ ...connectionTypeField, ...tcp }).map(([k, v], i) => [k, { ...v, order: 35 + i + 1 }])
    );
  }, [frogClients, t]);

  const handleChange = (name: string, value: string) => {
    fieldValuesObj[name] = value;
    if (name === 'parent_id' && value.includes('new-user-entry~')) {
      setAddControllerFields(Object.assign({}, addControllerFields, buildInlineCommLoopFields('tcp')));
      const portAndIp = value.split('~')[1].split(/[-:]/);
      fieldValuesObj['port'] = portAndIp.length > 1 ? portAndIp[1] : null;
      fieldValuesObj['ip_address'] = portAndIp[0];
    }
    else if (name === 'connection_type') {
      const connType = value as 'tcp' | 'serial';
      const current = Object.assign({}, addControllerFields);
      ['frog_client_id', 'ip_address', 'port', 'serial_port', 'baud_rate', 'data_bits', 'parity', 'stop_bits', 'protocol', 'connection_type'].forEach(k => delete current[k]);
      setAddControllerFields(Object.assign({}, current, buildInlineCommLoopFields(connType)));
    }
    else if (name === 'parent_id' && value !== 'null') {
      delete addControllerFields['ip_address'];
      delete addControllerFields['port'];
      delete addControllerFields['frog_client_id'];
      delete addControllerFields['serial_port'];
      delete addControllerFields['baud_rate'];
      delete addControllerFields['data_bits'];
      delete addControllerFields['parity'];
      delete addControllerFields['stop_bits'];
      delete addControllerFields['protocol'];
      delete addControllerFields['connection_type'];
      delete filledValuesObj['ip_address'];
      delete filledValuesObj['port'];
      delete filledValuesObj['frog_client_id'];
      setAddControllerFields(Object.assign({}, addControllerFields, {}));
    }
    if (name === 'device_factory' && (value === 'NVent920' || value === 'NVent910')) {
      (addControllerFields as any).subaddress = {
        type: 'number',
        label: 'Subaddress',
        errorText: 'Required',
        order: 90
      };
      setAddControllerFields(addControllerFields);
    }
    else if (name === 'device_factory') {
      delete addControllerFields['subaddress'];
    }

    if (name === 'device_factory' && value === 'NVentNGC40Controller') {
      delete addControllerFields['control_panel_id'];
    }
    else if (name === 'device_factory' && value !== 'NVentNGC40Controller') {
      (addControllerFields as any).control_panel_id = {
        type: 'autoCompleteSelect',
        label: 'Panel',
        options: [],
        deviceType: 'panel',
        optionsSelector: getControlPanelTags,
        freeSolo: true,
        formatOption: (option) => ({
          label: option.get('tag'),
          value: option.get('id')
        }),
        onValueChange,
        order: 20,
      };

      setAddControllerFields(addControllerFields);
    }
    setFilledValuesObj(Object.assign({}, filledValuesObj, fieldValuesObj));
  };

  useEffect(() => {
    if (!settingsModal?.get('isOpen')) {
      setAddControllerFields(commonControllerFields);
      setFilledValuesObj({});
    } else {
      if (newPanelId && panelDataFromDirectory) {
        setFilledValuesObj({ device_factory: panelDataFromDirectory?.get('device_factory') });
      }
    }
  }, [settingsModal, commonControllerFields, dispatch]);

  const handleClose = useCallback(() => {
    setAddControllerFields(commonControllerFields);
    setFilledValuesObj({});
    dispatch(setSettingsModalClose(null));
    if (newPanelId) {
      dispatch(pushHistory('/devices/list'));
    }
  }, [commonControllerFields, dispatch]);

  const addControllerModalSubmit = useCallback((rec) => {
    const address = parseInt(rec.get('address'), 10);
    let subaddress = rec.get('subaddress', null);
    if (subaddress) {
      subaddress = parseInt(subaddress, 10);
    }
    let port = rec.get('port', null);
    if (port) {
      port = parseInt(port, 10);
    }
    const parentId = rec.get('parent_id') === 'null' ? null : rec.get('parent_id');
    rec = rec.set('address', address);
    rec = rec.set('subaddress', subaddress);
    rec = rec.set('port', port);
    if (parentId?.includes('new-user-entry~')) {
      rec = rec.delete('control_panel_id');
      rec = rec.delete('parent_id');
      rec = rec.delete('connection_type');
    } else {
      rec = rec.set('control_panel_id', panelId);
    }
    dispatch(addController(rec));
  }, [dispatch, panelId]);

  return (
    <>
      {
        showSyncPopUp ?
          <InitDeviceModel />
          :
          null
      }

      <SettingsModal
        open={settingsModal?.get('isOpen') && settingsModal?.get('id') === SettingsModalTypes.ADD_CONTROLLER}
        handleClose={handleClose}
        handleSubmit={addControllerModalSubmit}
        handleChange={handleChange}
        title={'Add New Controller'}
        loading={settingsModal?.get('loading')}
        settings={fromJS(filledValuesObj)}
        fields={
          fromJS(addControllerFields)
        }
      />
    </>
  );
}

AddControllerModal.propTypes = {
};

AddControllerModal.defaultProps = {
};

export default React.memo(AddControllerModal);
